// CodiChat.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Fade,
    Grow,
    Skeleton,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import DirectionsIcon from '@mui/icons-material/Directions';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import { useUser } from './userContext';
import { useThemeContext, THEME_CLASSIC } from '../theme/ThemeContext';
import {
    DrkDialog,
    DrkDialogCard,
    DrkInfoBox
} from './DrkComponents';
import CodiMessageBubble from './CodiMessageBubble';
import CodiSessionList from './CodiSessionList';
import CodiInputBar from './CodiInputBar';
import { useNavigate } from 'react-router-dom';
import CodiWord from './CodiWord';


/**
 * Animation Keyframes für Typing Indicator
 */
const typingDotAnimation = {
    '@keyframes typingDot': {
        '0%, 60%, 100%': {
            opacity: 0.4,
            transform: 'translateY(0) scale(1)',
        },
        '30%': {
            opacity: 1,
            transform: 'translateY(-10px) scale(1.2)',
        },
    },
};


/**
 * Vordefinierte Quick Prompts für den Welcome Screen
 */
const quickPrompts = [
    {
        label: 'Wie erstelle ich einen Transport?',
        text: 'Wie erstelle ich einen Transport?',
        icon: <LocalShippingIcon fontSize="small" />
    },
    {
        label: 'Wie kann ich ein Telefon koppeln?',
        text: 'Wie kann ich ein mobiles Gerät registrieren? Stichwort Mobil-App',
        icon: <DirectionsIcon fontSize="small" />
    },
    {
        label: 'Wie funktioniert der Dilemma-Check?',
        text: 'Wie funktioniert der Dilemma-Check? Also das Punktesystem?',
        icon: <TipsAndUpdatesIcon fontSize="small" />
    },
    {
        label: 'Welchen Status kann ein Transport haben?',
        text: 'Welchen Status kann ein Transport haben?',
        icon: <FeaturedPlayListIcon fontSize="small" />
    },
    {
        label: 'Wie funktioniert codi?',
        text: 'Wie funktioniert codi und wie verwende ich das?',
        icon: <HelpOutlineIcon fontSize="small" />
    },
];

/**
 * Placeholder-Texte für animiertes Input-Field
 */
const placeholderTexts = [
    'Zeige mir alle Transport-Stati...',
    'Wie werden die Strafpunkte berechnet?',
    'Was sind Serientransporte?',
    'Wo finde ich meine Benachrichtigungen?',
    'Wie erstelle ich einen neuen Transport?',
    'Erkläre mir den Optimierungs-Algorithmus...',
    'Wie funktioniert die Fahrzeugverwaltung?',
];

/**
 * CodiChatButton - Button mit Chat-Icon, der den Chat-Dialog öffnet
 */
export const CodiChatButton = ({
    position = 'topRight',
    size = 'medium',
    inHeader = false,
    ...props
}) => {
    const [openChatDialog, setOpenChatDialog] = useState(false);
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const { sx: sxProp, ...restProps } = props;

    const positionStyles = {
        topRight: { position: 'absolute', top: 16, right: 16 },
        topLeft: { position: 'absolute', top: 16, left: 16 },
        bottomRight: { position: 'absolute', bottom: 16, right: 16 },
        bottomLeft: { position: 'absolute', bottom: 16, left: 16 },
        inline: { display: 'inline-flex', marginLeft: 8 }
    };

    const iconSize = {
        small: { size: 'small', iconSize: 18 },
        medium: { size: 'medium', iconSize: 24 },
        large: { size: 'large', iconSize: 32 }
    };

    // Light theme defaults (kept as-is), dark handled via sx theme callback
    const hoverBackgroundColor = !isClassic && inHeader
        ? '#e3f2fd'
        : !isClassic
            ? 'rgba(33, 150, 243, 0.1)'
            : 'transparent';

    const backgroundColor = !isClassic && inHeader
        ? '#bbdefb'
        : !isClassic
            ? 'rgba(33, 150, 243, 0.2)'
            : 'rgba(0, 0, 0, 0.04)';

    const handleOpenChatDialog = () => {
        setOpenChatDialog(true);
    };

    const handleCloseChatDialog = () => {
        setOpenChatDialog(false);
    };

    return (
        <>
            <Tooltip title="Frag codi">
                <IconButton
                    color="primary"
                    onClick={handleOpenChatDialog}
                    size={iconSize[size].size}
                    aria-label="Frag codi"
                    sx={(theme) => ({
                        ...positionStyles[position],
                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.action.selected : backgroundColor,
                        '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.action.hover : hoverBackgroundColor,
                        },
                        ...(inHeader && {
                            p: 0.75,
                            borderRadius: '50%',
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                        }),
                        ...sxProp
                    })}
                    {...restProps}
                >
                    <ChatIcon sx={{ fontSize: iconSize[size].iconSize }} />
                </IconButton>
            </Tooltip>

            {openChatDialog && (
                <CodiChatDialog
                    open={openChatDialog}
                    onClose={handleCloseChatDialog}
                />
            )}
        </>
    );
};

/**
 * CodiChatDialog - Chat-Dialog für Konversationen mit dem KI-Assistenten
 */
export const CodiChatDialog = ({ open, onClose }) => {
    const { user } = useUser();
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const navigate = useNavigate();

    const [sessions, setSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [thinking, setThinking] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const [placeholderFade, setPlaceholderFade] = useState(true);
    const [fullscreen, setFullscreen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [codiReady, setCodiReady] = useState(false);
    const [codiStatusLoading, setCodiStatusLoading] = useState(false);
    const [codiStatusMeta, setCodiStatusMeta] = useState(null);
    const [consentAccepted, setConsentAccepted] = useState(() => {
        try { return localStorage.getItem('codi_consent_v1') === '1'; } catch { return false; }
    });
    const [consentChecked, setConsentChecked] = useState(false);

    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const [isUserAtBottom, setIsUserAtBottom] = useState(true);
    const abortControllerRef = useRef(null);
    const autoSessionCreatedRef = useRef(false);

    const handleCreateSession = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URI}/api/codi/sessions`, {
                method: 'POST',
                headers: {
                    'Authorization': user.api_key,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const result = await response.json();
            const newId = result?.data?.session_id;
            if (newId) {
                setCurrentSessionId(newId);
                setMessages([]);
                await loadSessionsQuiet();
            }
        } catch (e) {
            console.error('Neue Session fehlgeschlagen:', e);
            setError('Neue Session konnte nicht erstellt werden: ' + e.message);
        }
    };

    const handleDeleteSession = async (sessionId) => {
        if (!sessionId) return;
        const yes = window.confirm('Diesen Chat wirklich löschen?');
        if (!yes) return;
        try {
            const resp = await fetch(`${process.env.REACT_APP_API_URI}/api/codi/sessions/${sessionId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': user.api_key,
                    'Content-Type': 'application/json'
                }
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            // Lokal aktualisieren
            setSessions(prev => (Array.isArray(prev) ? prev.filter(s => s.session_id !== sessionId) : prev));
            if (currentSessionId === sessionId) {
                // Wähle eine verbleibende Session oder leere Ansicht
                setCurrentSessionId(null);
                setMessages([]);
                await loadSessionsQuiet();
            }
        } catch (e) {
            console.error('Session löschen fehlgeschlagen:', e);
            setError('Chat konnte nicht gelöscht werden: ' + e.message);
        }
    };

    const groupedSessions = useMemo(() => {
        const byDay = new Map();
        const today = new Date(); today.setHours(0,0,0,0);
        const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
        const labelFor = (d) => {
            const dd = new Date(d);
            const day = new Date(dd); day.setHours(0,0,0,0);
            if (day.getTime() === today.getTime()) return 'Heute';
            if (day.getTime() === yesterday.getTime()) return 'Gestern';
            return dd.toLocaleDateString('de-DE');
        };
        const sorted = [...(sessions || [])].sort((a, b) => {
            const da = new Date(a.updated_at || a.created_at);
            const db = new Date(b.updated_at || b.created_at);
            return db - da;
        });
        sorted.forEach((s) => {
            const d = s.updated_at || s.created_at;
            const lab = labelFor(d);
            if (!byDay.has(lab)) byDay.set(lab, []);
            byDay.get(lab).push(s);
        });
        return Array.from(byDay.entries()).map(([label, items]) => ({ label, items }));
    }, [sessions]);

    // Load sessions on mount
    useEffect(() => {
        if (open) {
            checkCodiStatus();
            loadSessions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Load messages when session changes
    useEffect(() => {
        if (currentSessionId) {
            loadMessages(currentSessionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSessionId]);

    // Auto-scroll to bottom when messages change (only if user is near bottom)
    useEffect(() => {
        if (isUserAtBottom) {
            scrollToBottom();
        }
    }, [messages, isUserAtBottom]);

    // Rotate placeholder texts only on welcome screen
    useEffect(() => {
        if (messages.length > 0) return; // Only rotate on welcome screen

        let timeoutHandle = null;
        const interval = setInterval(() => {
            setPlaceholderFade(false);

            timeoutHandle = setTimeout(() => {
                setPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
                setPlaceholderFade(true);
            }, 600); // Wait for fade-out before changing text
        }, 3500); // Change every 3.5 seconds

        return () => {
            clearInterval(interval);
            if (timeoutHandle) clearTimeout(timeoutHandle);
        };
    }, [messages.length]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleMessagesScroll = () => {
        const el = messagesContainerRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        // consider within 40px as "at bottom"
        setIsUserAtBottom(distanceFromBottom < 40);
    };

    const loadSessions = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/api/codi/sessions`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const list = result.data || [];
            setSessions(list);

            // Automatisch neue Session anlegen, wenn letzte Sitzung älter als 1h ist
            if (!currentSessionId) {
                if (!Array.isArray(list) || list.length === 0) {
                    if (!autoSessionCreatedRef.current) {
                        autoSessionCreatedRef.current = true;
                        await handleCreateSession();
                        return;
                    }
                } else {
                    const top = list[0];
                    const ts = new Date(top.updated_at || top.created_at);
                    const ageMs = Date.now() - ts.getTime();
                    if (ageMs > 60 * 60 * 1000) { // > 1 Stunde
                        if (!autoSessionCreatedRef.current) {
                            autoSessionCreatedRef.current = true;
                            await handleCreateSession();
                            return;
                        }
                    } else {
                        setCurrentSessionId(top.session_id);
                    }
                }
            }
        } catch (err) {
            console.error('Fehler beim Laden der Sessions:', err);
            setError('Fehler beim Laden der Chat-Historie: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Load sessions without changing currentSessionId
    const loadSessionsQuiet = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/api/codi/sessions`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            setSessions(result.data || []);
            // Don't change currentSessionId - just update the list
        } catch (err) {
            console.error('Fehler beim Laden der Sessions:', err);
        }
    };

    const loadMessages = async (sessionId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/api/codi/sessions/${sessionId}/messages`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const data = Array.isArray(result.data) ? result.data : [];
            // Mark assistant messages as complete so citations + toolbar show after reload
            const normalized = data.map(m => m.role === 'assistant' ? { ...m, complete: true } : m);
            setMessages(normalized);
        } catch (err) {
            console.error('Fehler beim Laden der Nachrichten:', err);
            setError('Fehler beim Laden der Nachrichten: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const checkCodiStatus = async () => {
        try {
            setCodiStatusLoading(true);
            const resp = await fetch(`${process.env.REACT_APP_API_URI}/api/codi/status`, {
                method: 'GET',
                headers: {
                    'Authorization': user.api_key,
                    'Content-Type': 'application/json'
                }
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            const meta = data?.data || {};
            setCodiStatusMeta(meta);
            const createdOk = !!meta.created_at;
            const hasCount = typeof meta.count === 'number' ? meta.count > 0 : true; // wenn count fehlt, aber meta existiert
            setCodiReady(!!(createdOk && hasCount));
        } catch (e) {
            setCodiReady(false);
        } finally {
            setCodiStatusLoading(false);
        }
    };

    const handleSendMessage = async (messageText = null) => {
        if (!codiReady) {
            setError('codi ist derzeit nicht verfügbar (Index wird aufgebaut). Bitte später erneut versuchen.');
            return;
        }
        // Wenn messageText ein String ist, verwenden, sonst userInput
        const textToSend = (typeof messageText === 'string' && messageText) ? messageText : userInput;
        if (!textToSend.trim() || streaming) return;

        const userMessage = {
            role: 'user',
            content: textToSend,
            timestamp: new Date().toISOString()
        };

        // Add user message to display
        setMessages(prev => [...prev, userMessage]);
        setUserInput('');
        setStreaming(true);
        setError(null);

        // Create placeholder for assistant message
        const assistantMessageId = Date.now();
        const assistantMessage = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            timestamp: new Date().toISOString(),
            citations: []
        };

        setMessages(prev => [...prev, assistantMessage]);

        try {
            setThinking(false);
            abortControllerRef.current = new AbortController();

            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/api/codi/chat/stream`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        session_id: currentSessionId,
                        messages: [{ role: 'user', content: userMessage.content }]
                    }),
                    signal: abortControllerRef.current.signal
                }
            );
            if (!response.ok) {
                // Versuche, Server-Fehlermeldung zu lesen
                let serverMsg = '';
                try { serverMsg = (await response.json())?.message || ''; } catch (_) {}
                if (response.status === 400) {
                    // Zeige eine normale Assistenten-Antwort statt Fehlerbox
                    const friendly = 'Tut mir leid, ich kann keine personenbezogenen Daten verarbeiten. Was kann ich sonst für dich tun?';
                    setMessages(prev => prev.map(msg => (
                        msg.id === assistantMessageId
                            ? { ...msg, content: friendly, complete: true }
                            : msg
                    )));
                    // Sessions aktualisieren, Stream beenden
                    try { await loadSessionsQuiet(); } catch (_) {}
                    setStreaming(false);
                    abortControllerRef.current = null;
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}${serverMsg ? ' - ' + serverMsg : ''}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let pendingCitations = [];
            let pendingMeta = null;
            let finished = false;

            while (!finished) {
                const { done, value } = await reader.read();

                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');

                // Keep the last incomplete line in buffer
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            if (data.type === 'chunk') {
                                // Append chunk to assistant message
                                setMessages(prev => prev.map(msg =>
                                    msg.id === assistantMessageId
                                        ? { ...msg, content: msg.content + data.content }
                                        : msg
                                ));
                            } else if (data.type === 'session') {
                                // Update session_id
                                setCurrentSessionId(data.session_id);
                            } else if (data.type === 'citations') {
                                // Store citations; show after completion
                                pendingCitations = data.citations || [];
                            } else if (data.type === 'usage') {
                                // Attach usage to assistant placeholder
                                setMessages(prev => prev.map(msg =>
                                    msg.id === assistantMessageId
                                        ? { ...msg, usage: data.usage }
                                        : msg
                                ));
                            } else if (data.type === 'meta') {
                                // Attach meta fields (model, endpoint, etc.)
                                pendingMeta = data.meta || null;
                                // eslint-disable-next-line no-loop-func
                                setMessages(prev => prev.map(msg =>
                                    msg.id === assistantMessageId
                                        ? { ...msg, meta: pendingMeta }
                                        : msg
                                ));
                            } else if (data.type === 'thinking') {
                                setThinking(!!data.active);
                            } else if (data.type === 'done') {
                                // Stream finished
                                // Apply citations now and mark as complete
                                // eslint-disable-next-line no-loop-func
                                setMessages(prev => prev.map(msg =>
                                    msg.id === assistantMessageId
                                        ? { ...msg, citations: pendingCitations, complete: true }
                                        : msg
                                ));
                                finished = true;
                                try { await reader.cancel(); } catch (_) {}
                                break;
                            } else if (data.type === 'error') {
                                const msg = data.error || data.message || 'Unbekannter Fehler';
                                throw new Error(msg);
                            }
                        } catch (parseErr) {
                            console.error('Fehler beim Parsen der SSE-Daten:', parseErr);
                        }
                    }
                }
            }

            // Reload sessions to get updated list (without changing currentSessionId)
            await loadSessionsQuiet();

        } catch (err) {
            if (err.name === 'AbortError') {
                console.log('Stream aborted');
            } else {
                console.error('Fehler beim Senden der Nachricht:', err);
                // Wenn der Assistenten-Platzhalter noch leer ist, ersetze ihn durch eine sanfte Antwort
                setMessages(prev => {
                    const hasAssistant = prev.some(m => m.id === assistantMessageId && m.role === 'assistant');
                    if (hasAssistant) {
                        return prev.map(m => (
                            m.id === assistantMessageId
                                ? { ...m, content: 'Entschuldige, hier ist ein Fehler aufgetreten. Bitte versuche es später erneut.', complete: true }
                                : m
                        ));
                    }
                    // Fallback: zeige Fehlerbox
                    setError('Fehler beim Senden der Nachricht: ' + err.message);
                    return prev;
                });
            }
        } finally {
            setStreaming(false);
            setThinking(false);
            abortControllerRef.current = null;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleStopStreaming = () => {
        if (abortControllerRef.current) {
            try {
                abortControllerRef.current.abort();
                setStreaming(false);
            } catch (e) {
                console.error('Fehler beim Abbrechen:', e);
            }
        }
    };

    const handleRegenerateResponse = async (messageIndex) => {
        // Finde die letzte User-Nachricht vor dieser Assistant-Nachricht
        const userMessages = messages.slice(0, messageIndex).filter(m => m.role === 'user');
        if (userMessages.length === 0) return;

        const lastUserMessage = userMessages[userMessages.length - 1];

        // Entferne alle Nachrichten ab dieser Assistant-Nachricht
        setMessages(prev => prev.slice(0, messageIndex));

        // Sende die Nachricht neu
        await handleSendMessage(lastUserMessage.content);
    };

    const handleRateMessage = async (messageIndex, ratingValue) => {
        try {
            const msg = messages[messageIndex];
            if (!msg || msg.role !== 'assistant' || !msg.complete) return;

            // Determine message id (from server). If not present, resolve latest assistant id via fetch
            let targetId = (typeof msg.id === 'number' && msg.id > 0 && msg.id <= 2147483647) ? msg.id : null;
            if (!targetId && currentSessionId) {
                const resp = await fetch(
                    `${process.env.REACT_APP_API_URI}/api/codi/sessions/${currentSessionId}/messages`,
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': user.api_key,
                            'Content-Type': 'application/json'
                        }
                    }
                );
                if (resp.ok) {
                    const data = await resp.json();
                    const list = Array.isArray(data?.data) ? data.data : [];
                    // take the last assistant message
                    for (let i = list.length - 1; i >= 0; i--) {
                        if (list[i].role === 'assistant' && typeof list[i].id === 'number') {
                            targetId = list[i].id;
                            break;
                        }
                    }
                }
            }

            if (!targetId) return;

            const putResp = await fetch(
                `${process.env.REACT_APP_API_URI}/api/codi/messages/${targetId}/rating`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ rating: ratingValue })
                }
            );
            if (!putResp.ok) {
                console.error('Fehler beim Speichern der Bewertung:', putResp.status);
                return;
            }
            // Update local state
            setMessages(prev => prev.map((m, idx) => idx === messageIndex ? { ...m, rating: ratingValue } : m));
        } catch (e) {
            console.error('Fehler beim Bewerten:', e);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';

        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'gerade eben';
        if (diffMins < 60) return `vor ${diffMins} Minute${diffMins > 1 ? 'n' : ''}`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `vor ${diffHours} Stunde${diffHours > 1 ? 'n' : ''}`;

        const diffDays = Math.floor(diffHours / 24);
        return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    };

    const renderMessage = (message, index) => {
        const stableKey = (message && (message.id ?? message.timestamp)) ?? index;
        return (
            <CodiMessageBubble
                key={stableKey}
                message={message}
                index={index}
                isClassic={isClassic}
                streaming={streaming}
                formatTimestamp={formatTimestamp}
                onRegenerate={handleRegenerateResponse}
                onRate={handleRateMessage}
                onNavigate={(url) => navigate(url)}
                onCloseDialog={onClose}
            />
        );
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && open) {
                handleClose();
            }
        };

        if (open) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Ensure stream is aborted on unmount/close
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                try { abortControllerRef.current.abort(); } catch (e) {}
            }
        };
    }, []);

    const handleClose = () => {
        if (abortControllerRef.current) {
            try { abortControllerRef.current.abort(); } catch (e) {}
        }
        setStreaming(false);
        onClose && onClose();
    };

    const handleAcceptConsent = () => {
        if (!consentChecked) return;
        try { localStorage.setItem('codi_consent_v1', '1'); } catch {}
        setConsentAccepted(true);
        // Serverseitig speichern (best effort)
        try {
            fetch(`${process.env.REACT_APP_API_URI}/api/codi/consent`, {
                method: 'POST',
                headers: {
                    'Authorization': user.api_key,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ policy_key: 'codi_patient_privacy', policy_version: 'v1' })
            }).catch(() => {});
        } catch (_) {}
    };

    // Beim Öffnen Consent serverseitig prüfen, um erneute Fragen zu vermeiden (geräteübergreifend)
    useEffect(() => {
        let aborted = false;
        async function checkConsent() {
            try {
                if (!open || !user || !user.api_key) return;
                const resp = await fetch(`${process.env.REACT_APP_API_URI}/api/codi/consent?policy_key=codi_patient_privacy&policy_version=v1`, {
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                });
                if (!resp.ok) return;
                const data = await resp.json();
                if (!aborted && data?.data?.accepted) {
                    setConsentAccepted(true);
                    try { localStorage.setItem('codi_consent_v1', '1'); } catch {}
                } else if (!aborted) {
                    // Server ist Quelle der Wahrheit: keine Zustimmung vorhanden -> neu fragen
                    setConsentAccepted(false);
                    try { localStorage.removeItem('codi_consent_v1'); } catch {}
                }
            } catch (_) {}
        }
        checkConsent();
        return () => { aborted = true; };
    }, [open, user]);

    const dialogActions = (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
                KI-Hinweis: codi kann Fehler machen und gelegentlich halluzinieren.
            </Typography>
            <Button color="inherit" onClick={handleClose} startIcon={<CloseIcon />}>
                Schließen
            </Button>
        </Box>
    );

    return (
        <DrkDialog
            open={open}
            onClose={handleClose}
            title="codi - Dein KI-Assistent"
            maxWidth={fullscreen ? false : "lg"}
            fullWidth
            fullScreen={fullscreen}
            disableAutoFocus
            actions={dialogActions}
            headerSx={(theme) => ({
                // Dark Mode: dezente Orange-Akzentierung auf dunklem Paper
                ...(theme.palette.mode === 'dark'
                    ? {
                        backgroundColor: theme.palette.background.paper,
                        backgroundImage: 'linear-gradient(to bottom, rgba(255,107,71,0.18) 0%, rgba(255,107,71,0.10) 40%, rgba(255,107,71,0.00) 100%)',
                        boxShadow: 'inset 0 -1px 0 rgba(255,107,71,0.35), 0 4px 10px rgba(0,0,0,0.5)'
                    }
                    : {
                        background: 'linear-gradient(135deg, #4A90E2 0%, #5E72E4 50%, #6C5CE7 100%)',
                        boxShadow: '0 8px 32px rgba(74, 144, 226, 0.25)'
                    }
                ),
                backdropFilter: 'blur(10px)'
            })}
            headerActions={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title={fullscreen ? "Vollbild verlassen" : "Vollbild"}>
                        <IconButton onClick={() => setFullscreen(v => !v)} color="inherit" size="small" aria-label={fullscreen ? "Vollbild verlassen" : "Vollbild"}>
                            {fullscreen ? <FullscreenExitIcon sx={{ color: 'white' }} /> : <FullscreenIcon sx={{ color: 'white' }} />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Hilfe">
                        <IconButton onClick={() => setShowHelp(v => !v)} color="inherit" size="small" aria-label="Hilfe">
                            <HelpOutlineIcon sx={{ color: 'white' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            }
        >
            <DrkDialogCard>
                {error && (
                    <DrkInfoBox color="error" sx={{ mb: 2 }}>
                        <Typography color="error">{error}</Typography>
                    </DrkInfoBox>
                )}

                {/* Optionaler Hilfe-Abschnitt (nicht technisch) */}
                {!error && showHelp && (
                    <Box sx={{ mb: 2 }}>
                        <DrkInfoBox color="info">
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                So hilft dir codi
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                codi liest passende Inhalte aus unseren Hilfeseiten und Unterlagen und fasst sie für dich verständlich zusammen. Wenn möglich, zeigt er Quellen an und bezieht die aktuelle Seite mit ein.
                            </Typography>
                            <Typography variant="body2">
                                Datenschutz: Unser Wissensindex liegt auf coderis Servern in Deutschland. Deine Fragen werden nur zur Beantwortung in deiner Sitzung genutzt.
                            </Typography>
                        </DrkInfoBox>
                    </Box>
                )}

                {/* Split-View Layout: Session-Liste links, Chat rechts */}
                <Box sx={{ display: 'flex', gap: sidebarCollapsed ? 1 : 2, height: fullscreen ? 'calc(100vh - 250px)' : '70vh' }}>
                    {/* Linke Seite: Session-Liste */}
                    <Box
                        sx={{
                            width: sidebarCollapsed ? '40px' : '300px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: sidebarCollapsed ? 'center' : 'stretch',
                            borderRight: sidebarCollapsed ? 'none' : '1px solid',
                            borderColor: 'divider',
                            pr: sidebarCollapsed ? 0 : 2,
                            overflow: 'hidden',
                            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative'
                        }}
                    >
                        {/* Toggle Sidebar Button */}
                        <Tooltip title={sidebarCollapsed ? "Sidebar öffnen" : "Sidebar schließen"} placement="right">
                            <IconButton
                                onClick={() => setSidebarCollapsed(v => !v)}
                                aria-label={sidebarCollapsed ? "Sidebar öffnen" : "Sidebar schließen"}
                                sx={{
                                    alignSelf: sidebarCollapsed ? 'center' : 'flex-end',
                                    my: 0.5,
                                    color: 'primary.main',
                                    bgcolor: isClassic ? 'grey.100' : 'rgba(74, 144, 226, 0.1)',
                                    '&:hover': {
                                        bgcolor: isClassic ? 'grey.200' : 'rgba(74, 144, 226, 0.2)',
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                }}
                            >
                                {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
                            </IconButton>
                        </Tooltip>
                        {/* Neuer Chat Button in collapsed mode (unter Toggle) */}
                        {sidebarCollapsed && (
                            <Tooltip title="Neuer Chat" placement="right">
                                <IconButton
                                    onClick={handleCreateSession}
                                    aria-label="Neuer Chat"
                                    sx={{
                                        alignSelf: 'center',
                                        my: 0.5,
                                        color: 'primary.main',
                                        bgcolor: isClassic ? 'grey.100' : 'rgba(74, 144, 226, 0.1)',
                                        '&:hover': { bgcolor: isClassic ? 'grey.200' : 'rgba(74, 144, 226, 0.2)' }
                                    }}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        {!sidebarCollapsed && (
                            <CodiSessionList
                                groupedSessions={groupedSessions}
                                currentSessionId={currentSessionId}
                                onSelect={setCurrentSessionId}
                                onDelete={handleDeleteSession}
                                isClassic={isClassic}
                                showCreateButton={!sidebarCollapsed}
                                onCreate={handleCreateSession}
                            />
                        )}
                    </Box>

                    {/* Rechte Seite: Chat Area */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {/* Messages Area */}
                        <Box
                            sx={(theme) => ({
                                flex: 1,
                                overflowY: 'auto',
                                mb: 2,
                                p: 3,
                                bgcolor: theme.palette.mode === 'dark'
                                    ? theme.palette.background.paper
                                    : (isClassic ? 'background.paper' : 'rgba(248, 250, 252, 0.6)'),
                                backdropFilter: 'blur(10px)',
                                borderRadius: isClassic ? '4px' : '16px',
                                border: isClassic ? '1px solid' : 'none',
                                borderColor: 'divider',
                                boxShadow: isClassic ? 'none' : '0 2px 12px rgba(0, 0, 0, 0.04)',
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    bgcolor: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0, 0, 0, 0.2)',
                                    borderRadius: '4px',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0, 0, 0, 0.3)',
                                    }
                                }
                            })}
                            ref={messagesContainerRef}
                            onScroll={handleMessagesScroll}
                        >
                    {!consentAccepted ? (
                        <Box sx={{ p: 2 }}>
                            <DrkInfoBox color="warning" sx={{ p: 0 }}>
                                {/* Header */}
                                <Box sx={(theme) => ({
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 2, py: 1.5,
                                    borderBottom: '1px solid', borderColor: 'divider',
                                    background: theme.palette.mode === 'dark'
                                        ? 'linear-gradient(90deg, rgba(255,160,0,0.12) 0%, rgba(255,160,0,0.08) 100%)'
                                        : 'linear-gradient(90deg, rgba(255,243,224,1) 0%, rgba(255,249,232,1) 100%)'
                                })}>
                                    <PrivacyTipOutlinedIcon color="warning" />
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Willkommen bei codi</Typography>
                                </Box>

                                {/* Body */}
                                <Box sx={{ px: 2, py: 2 }}>
                                    <Typography variant="body1" sx={{ mb: 1.5 }}>
                                        Schön, dass du codi ausprobierst! Bevor wir starten, ein kurzes Wort zum Datenschutz:
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Box sx={{ flex: 1, minWidth: 260 }}>
                                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <ShieldOutlinedIcon fontSize="small" /> Warum ist das wichtig?
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                - KI‑Dienste werden teils von externen Anbietern betrieben. Dort können Anfragen geloggt oder zur Qualitätssicherung verarbeitet werden.<br/>
                                                - Personenbezogene Daten (z. B. Namen, Geburtsdaten, Versicherungsnummern) dürfen deshalb nicht übertragen werden.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 260 }}>
                                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <LightbulbOutlinedIcon fontSize="small" /> So nutzt du codi sinnvoll
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                - Frage allgemein zu Funktionen, Workflows und Begriffen.<br/>
                                                - Beschreibe Situationen ohne reale Namen oder eindeutige Personenmerkmale.
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Bitte gib daher <b>keine personenbezogenen Daten</b> ein. Wenn doch etwas erkannt wird, lehnt codi die Eingabe freundlich ab.
                                    </Typography>

                                    <FormControlLabel
                                        control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />}
                                        label={<Typography variant="body2">Ich habe den Hinweis gelesen und verstanden.</Typography>}
                                        sx={{ mb: 1 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button variant="contained" disabled={!consentChecked} onClick={handleAcceptConsent}>
                                            Einverstanden
                                        </Button>
                                        <Button
                                            variant="text"
                                            component="a"
                                            href="/datenschutz"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Mehr erfahren
                                        </Button>
                                    </Box>
                                </Box>
                            </DrkInfoBox>
                        </Box>
                    ) : (!codiReady && !codiStatusLoading) ? (
                    <Box sx={{ p: 2 }}>
                        <DrkInfoBox color="secondary">
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                codi momentan nicht verfügbar
                            </Typography>
                            {codiStatusMeta?.building?.running ? (
                                <>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Der KI‑Index wird für deinen Mandanten neu aufgebaut.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Fortschritt: {typeof codiStatusMeta.building.processed_docs === 'number' ? codiStatusMeta.building.processed_docs : '—'}
                                        {typeof codiStatusMeta.building.total_docs === 'number' ? ` / ${codiStatusMeta.building.total_docs}` : ''}
                                        {typeof codiStatusMeta.building.progress_percent === 'number' ? ` (${codiStatusMeta.building.progress_percent}% )` : ''}
                                    </Typography>
                                    {typeof codiStatusMeta.building.eta_seconds === 'number' && (
                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                            Geschätzte Restzeit: ~{Math.max(1, Math.round(codiStatusMeta.building.eta_seconds / 60))} Minuten
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Der KI‑Index wird aktuell aufgebaut oder ist nicht vorhanden. Bitte versuche es in Kürze erneut.
                                </Typography>
                            )}
                            <Button variant="outlined" onClick={checkCodiStatus} disabled={codiStatusLoading}>
                                Erneut prüfen
                            </Button>
                        </DrkInfoBox>
                    </Box>
                    ) : loading && messages.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                            <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: '16px' }} />
                            <Skeleton variant="rectangular" height={80} sx={{ mb: 2, borderRadius: '16px' }} />
                            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '16px' }} />
                        </Box>
                    ) : messages.length === 0 ? (
                        <Fade in={true} timeout={600}>
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                {/* Welcome Header */}
                                <Box sx={{ mb: 4 }}>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        p: 2,
                                        borderRadius: '50%',
                                        bgcolor: 'rgba(74, 144, 226, 0.1)',
                                        mb: 2,
                                        animation: 'welcomePulse 3s ease-in-out infinite',
                                        '@keyframes welcomePulse': {
                                            '0%, 100%': {
                                                boxShadow: '0 0 0 0 rgba(74, 144, 226, 0.4)',
                                                transform: 'scale(1)',
                                            },
                                            '50%': {
                                                boxShadow: '0 0 0 20px rgba(74, 144, 226, 0)',
                                                transform: 'scale(1.05)',
                                            }
                                        }
                                    }}>
                                        <LightbulbOutlinedIcon
                                            sx={{
                                                fontSize: 52,
                                                color: 'primary.main',
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h4"
                                        sx={(theme) => ({
                                            mb: 1.5,
                                            fontWeight: 600,
                                            color: theme.palette.mode === 'dark' ? '#f28424' : 'primary.main',
                                            '@keyframes waveText': {
                                                '0%, 100%': {
                                                    transform: 'translateY(0)',
                                                },
                                                '50%': {
                                                    transform: 'translateY(-1px)',
                                                },
                                            },
                                            '@keyframes colorShiftDark': {
                                                '0%': { color: '#f9ae1f' },
                                                '33%': { color: '#f28424' },
                                                '66%': { color: '#eb5b28' },
                                                '100%': { color: '#f9ae1f' },
                                            },
                                            '@keyframes colorShiftLight': {
                                                '0%': { color: '#4A90E2' },
                                                '33%': { color: '#5E72E4' },
                                                '66%': { color: '#6C5CE7' },
                                                '100%': { color: '#4A90E2' },
                                            },
                                            '& .welcome-wave > span': {
                                                display: 'inline-block',
                                                animation: `waveText 3s ease-in-out infinite, ${theme.palette.mode === 'dark' ? 'colorShiftDark' : 'colorShiftLight'} 3s ease-in-out infinite`,
                                            },
                                            '& .welcome-wave > span:nth-child(1)': { animationDelay: '0s' },
                                            '& .welcome-wave > span:nth-child(2)': { animationDelay: '0.05s' },
                                            '& .welcome-wave > span:nth-child(3)': { animationDelay: '0.1s' },
                                            '& .welcome-wave > span:nth-child(4)': { animationDelay: '0.15s' },
                                            '& .welcome-wave > span:nth-child(5)': { animationDelay: '0.2s' },
                                            '& .welcome-wave > span:nth-child(6)': { animationDelay: '0.25s' },
                                            '& .welcome-wave > span:nth-child(7)': { animationDelay: '0.3s' },
                                            '& .welcome-wave > span:nth-child(8)': { animationDelay: '0.35s' },
                                            '& .welcome-wave > span:nth-child(9)': { animationDelay: '0.4s' },
                                            '& .welcome-wave > span:nth-child(10)': { animationDelay: '0.45s' },
                                            '& .welcome-wave > span:nth-child(11)': { animationDelay: '0.5s' },
                                            '& .welcome-wave > span:nth-child(12)': { animationDelay: '0.55s' },
                                            '& .welcome-wave > span:nth-child(13)': { animationDelay: '0.6s' },
                                            '& .welcome-wave > span:nth-child(14)': { animationDelay: '0.65s' },
                                            '& .welcome-wave > span:nth-child(15)': { animationDelay: '0.7s' },
                                            '& .welcome-wave > span:nth-child(16)': { animationDelay: '0.75s' },
                                            '& .welcome-wave > span:nth-child(17)': { animationDelay: '0.8s' },
                                            '& .welcome-wave > span:nth-child(18)': { animationDelay: '0.85s' },
                                            '& .welcome-wave > span:nth-child(19)': { animationDelay: '0.9s' },
                                        })}
                                    >
                                        <span className="welcome-wave">
                                            {"Hallo! Ich bin".split('').map((char, index) => (
                                                <span key={index}>{char === ' ' ? '\u00A0' : char}</span>
                                            ))}
                                        </span>
                                        &nbsp;
                                        <CodiWord />
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" sx={{ fontWeight: 500 }}>
                                        Wie kann ich dir helfen?
                                    </Typography>
                                </Box>

                                {/* Quick Prompts */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        maxWidth: '600px',
                                        mx: 'auto'
                                    }}
                                >
                                    {quickPrompts.map((prompt, index) => (
                                        <Grow
                                            in={true}
                                            timeout={600 + (index * 100)}
                                            key={prompt.label}
                                        >
                                            <Chip
                                                label={prompt.label}
                                                icon={prompt.icon}
                                                onClick={() => {
                                                    handleSendMessage(prompt.text);
                                                }}
                                                clickable
                                                sx={(theme) => ({
                                                    py: 2.5,
                                                    px: 1,
                                                    height: 'auto',
                                                    fontSize: '0.875rem',
                                                    '& .MuiChip-label': {
                                                        whiteSpace: 'normal',
                                                        padding: '8px 12px'
                                                    },
                                                    bgcolor: theme.palette.mode === 'dark'
                                                        ? theme.palette.background.paper
                                                        : (isClassic ? 'grey.100' : 'rgba(255, 255, 255, 0.8)'),
                                                    border: '1px solid',
                                                    borderColor: 'rgba(74, 144, 226, 0.2)',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                                    backdropFilter: 'blur(10px)',
                                                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                    '&:hover': {
                                                        bgcolor: 'primary.main',
                                                        color: 'white',
                                                        borderColor: 'primary.main',
                                                        boxShadow: '0 8px 24px rgba(74, 144, 226, 0.4)',
                                                        transform: 'translateY(-4px) scale(1.02)',
                                                        '& .MuiChip-icon': {
                                                            color: 'white'
                                                        }
                                                    }
                                                })}
                                            />
                                        </Grow>
                                    ))}
                                </Box>
                            </Box>
                        </Fade>
                    ) : (
                        <>
                            {messages.map((message, index) => renderMessage(message, index))}
                            {streaming && (
                                <Fade in={true} timeout={400}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                                        <Box sx={(theme) => ({
                                            maxWidth: '85%',
                                            width: '100%',
                                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : (isClassic ? 'grey.50' : 'rgba(255, 255, 255, 0.6)'),
                                            borderRadius: isClassic ? '4px' : '20px',
                                            p: 2.5,
                                            backdropFilter: 'blur(10px)',
                                            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
                                        })}>
                                            {/* Loading Skeleton für kommende Nachricht */}
                                            <Box sx={{ mb: 1.5 }}>
                                                <Skeleton
                                                    variant="rectangular"
                                                    height={16}
                                                    sx={{
                                                        mb: 1,
                                                        borderRadius: '12px',
                                                        width: '90%',
                                                        opacity: 0.3
                                                    }}
                                                />
                                                <Skeleton
                                                    variant="rectangular"
                                                    height={16}
                                                    sx={{
                                                        mb: 1,
                                                        borderRadius: '12px',
                                                        width: '75%',
                                                        opacity: 0.25
                                                    }}
                                                />
                                                <Skeleton
                                                    variant="rectangular"
                                                    height={16}
                                                    sx={{
                                                        borderRadius: '12px',
                                                        width: '85%',
                                                        opacity: 0.2
                                                    }}
                                                />
                                            </Box>

                                            {/* Typing Indicator darunter */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    mt: 1.5,
                                                    pt: 1.5,
                                                    borderTop: '1px solid',
                                                    borderColor: 'divider'
                                                }}
                                            >
                                                {/* Animierte Typing Dots */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.6,
                                                        ...typingDotAnimation,
                                                    }}
                                                >
                                                    {[0, 1, 2].map((i) => (
                                                        <Box
                                                            key={i}
                                                            sx={{
                                                                width: 10,
                                                                height: 10,
                                                                borderRadius: '50%',
                                                                bgcolor: 'primary.main',
                                                                boxShadow: '0 2px 8px rgba(74, 144, 226, 0.4)',
                                                                animation: 'typingDot 1.4s infinite',
                                                                animationDelay: `${i * 0.2}s`,
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    sx={{
                                                        fontStyle: 'italic',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {`codi tippt...${thinking ? ' (denkt …)' : ''}`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Fade>
                            )}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                        </Box>

                        {/* Input Area */}
                        <CodiInputBar
                            isClassic={isClassic}
                            userInput={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={messages.length === 0 ? placeholderTexts[placeholderIndex] : 'Schreibe eine Nachricht...'}
                            placeholderFade={placeholderFade}
                            streaming={streaming}
                            disabled={!codiReady || codiStatusLoading || !consentAccepted}
                            onStop={handleStopStreaming}
                            onSend={() => handleSendMessage()}
                        />
                    </Box>
                </Box>
            </DrkDialogCard>
        </DrkDialog>
    );
};

const defaultExport = { CodiChatButton, CodiChatDialog };
export default defaultExport;
